import { SelectProps, Typography } from 'antd';
import type { RefSelectProps } from 'antd/lib/select';
import { ReactElement, useRef, VFC } from 'react';
import { useMeterModels } from 'hooks/meter_models/useMeterModels';
import { Manufacturer, MeterModel, SerializedMeterModel } from '@enmon/commons/lib/tools';
import { FormattedMessage } from 'react-intl';
import { Commodity } from '@enmon/commons/lib/shared';
import { Nullable } from '@enmon/commons';
import { groupModelsByManufacturerFamily } from 'utils/manufacturerFamily';
import { Select } from './Select';
import { SelectMessages as messages } from './messages';

interface MeterModelSelectProps extends SelectProps {
  commodity: Nullable<Commodity>;
  manufacturer: Manufacturer['id'] | undefined;
  value?: MeterModel['id'];
  onChange?: (value: MeterModel['id'] | null) => void;
  onModelManufacturerChange?: (manufacturerId: Manufacturer['id']) => void;
}

const toOption = ({ id, name }: SerializedMeterModel) => ({ label: name, value: id });

export const MeterModelSelect: VFC<MeterModelSelectProps> = ({
  commodity,
  manufacturer,
  onChange,
  onDropdownVisibleChange,
  onModelManufacturerChange,
  ...restProps
}) => {
  const selectRef = useRef<RefSelectProps>(null);
  const { isLoading: isMeterModelsLoading, meterModels } = useMeterModels(commodity);

  const groups = groupModelsByManufacturerFamily(meterModels, manufacturer);
  const isFamilyExtended = groups.length > 1;

  const options = groups.map(group => ({ label: group.manufacturer.name, options: group.models.map(toOption) }));

  const handleDropdownVisibleChange = (open: boolean) => {
    onDropdownVisibleChange?.(open);
    // always start at the top
    if (open) setTimeout(() => setTimeout(() => selectRef.current?.scrollTo(0)));
  };

  const handleChange = (value: MeterModel['id'] | undefined) => {
    onChange?.(value ?? null);

    const ownerId = groups.find(group => group.models.some(model => model.id === value))?.manufacturer.id;

    if (manufacturer && ownerId && ownerId !== manufacturer) {
      onModelManufacturerChange?.(ownerId);
    }
  };

  const selectDropdown = (menu: ReactElement) =>
    isFamilyExtended ? (
      <>
        <Typography.Text style={{ display: 'block', padding: '4px 12px 8px' }} type="secondary">
          <FormattedMessage
            {...messages.meterModelFamilyHint}
            values={{ manufacturers: groups.map(group => group.manufacturer.name).join(', ') }}
          />
        </Typography.Text>
        {menu}
      </>
    ) : (
      menu
    );

  return (
    <Select
      allowClear
      showSearch
      className="w-100"
      disabled={!manufacturer}
      dropdownRender={selectDropdown}
      filterOption={(input, option) =>
        (option?.label?.toString() ?? '').toLowerCase().indexOf(input.toLowerCase()) >= 0
      }
      loading={isMeterModelsLoading}
      options={options}
      placeholder={<FormattedMessage {...messages.meterModelPlaceholder} />}
      {...restProps}
      selectRef={selectRef}
      onChange={handleChange}
      onDropdownVisibleChange={handleDropdownVisibleChange}
    />
  );
};
